import { useMutation as useApolloMutation } from '@apollo/client';
import {
  useMutation as useTanstackMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { useIsFeatureFlagEnabled } from '../../feature-flags/useIsFeatureFlagEnabled';
import { useTRPC } from '../trpc';
import type {
  MutationHookConfig,
  MutationHookFactoryResult,
  MutationHookResult,
} from './types';

/**
 * Portal-local mirror of the web `createMutationHook` (RSP-6570), trimmed to
 * the portal's needs (no change-request gating, no Apollo cache eviction). It
 * collapses the previous two-file pattern (`useXxx.tsx` wrapper +
 * `useXxxTRPC.tsx`) into a single declarative config, switching between the
 * Apollo mutation (flag off) and the tRPC mutation (flag on) on the `trpc`
 * feature flag and preserving the per-entity method name so consumers are
 * untouched. It adds no UI side-effects (toasts) and never passes
 * `throwOnError` — the form owns mutation UX.
 *
 * Counterpart: `packages/web/src/utils/hook-factories/createMutationHook.tsx`. The
 * duplication is deliberate and time-boxed — both copies exist in order to be
 * deleted, and they retire together when the `trpc` flag is removed and Apollo
 * comes out of the portal. Unifying them earlier means either dragging web's
 * change-request machinery into the portal bundle or building a config surface
 * to express the divergences. Keep them in sync only where behaviour matters.
 */
export const createMutationHook = <
  TInput,
  TTrpcInput,
  TTrpcResult,
  TGraphQLOutput,
  TName extends string,
  TOptions = void,
>(
  config: MutationHookConfig<
    TInput,
    TTrpcInput,
    TTrpcResult,
    TGraphQLOutput,
    TName,
    TOptions
  >
): MutationHookFactoryResult<TName, TInput, TGraphQLOutput, TOptions> => {
  const {
    name,
    mutationOptions,
    mapInput,
    mapResponse,
    invalidate,
    graphqlDocument,
    graphqlVariables,
  } = config;

  return () => {
    const trpcEnabled = useIsFeatureFlagEnabled('trpc');
    const trpc = useTRPC();
    const queryClient = useQueryClient();

    // GraphQL mutation (flag off).
    const [graphqlMutation, graphqlState] = useApolloMutation(graphqlDocument);

    // tRPC mutation (flag on).
    // `invalidateQueries` awaits the active refetches it triggers, so the
    // mutation does not resolve until they land. Fire the families
    // concurrently rather than in sequence — serialising them stacked a
    // round-trip per family onto perceived save latency, and the portal
    // navigates away from the page those refetches serve.
    const onSuccess = async (): Promise<void> => {
      await Promise.all(
        (invalidate?.(trpc) ?? []).map((queryKey) =>
          queryClient.invalidateQueries({ queryKey })
        )
      );
    };
    const trpcMutation = useTanstackMutation(mutationOptions(trpc, onSuccess));

    const run = async (
      input: TInput,
      options?: TOptions
    ): Promise<TGraphQLOutput> => {
      if (trpcEnabled) {
        const result = await trpcMutation.mutateAsync(mapInput(input));

        return mapResponse(result, input);
      }

      const graphqlResult = await graphqlMutation({
        variables: graphqlVariables(input, options),
      });
      if (!graphqlResult.data) {
        throw new Error(`Failed to run ${name}`);
      }

      return graphqlResult.data;
    };

    // The mapped return type names the mutate fn per-entity (e.g.
    // `updateResponse`); a computed-key object literal can't be inferred as
    // that mapped type, so assert the assembled shape.
    return {
      [name]: run,
      loading: trpcEnabled ? trpcMutation.isPending : graphqlState.loading,
      error: trpcEnabled ? trpcMutation.error : (graphqlState.error ?? null),
    } as MutationHookResult<TName, TInput, TGraphQLOutput, TOptions>;
  };
};
